import config from "./configuration";

export class ApiError extends Error {
  constructor(message, { errors = [], status, network = false } = {}) {
    super(message);
    this.name = "ApiError";
    this.errors = errors;
    this.status = status;
    this.network = network;
    this.fieldErrors = {};
    errors.forEach((item) => {
      if (item && item.field && !this.fieldErrors[item.field]) this.fieldErrors[item.field] = item.message;
    });
  }
}

function token() {
  try {
    const raw = JSON.parse(localStorage.getItem("userData") || "{}");
    return raw.token || "";
  } catch (err) {
    return "";
  }
}

function queryString(query) {
  if (!query) return "";
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") params.set(key, value);
  });
  const text = params.toString();
  return text ? `?${text}` : "";
}

async function request(path, { method = "GET", body, query, file = false, root = "api/accounts" } = {}) {
  let response;
  try {
    response = await fetch(`${config.BASE_URL}${root}${path}${queryString(query)}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token()}`,
      },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    throw new ApiError("Could not reach the server. Check that the API is running.", { network: true });
  }

  if (response.status === 401) {
    localStorage.setItem("token", "");
    localStorage.setItem("userData", "");
    window.location = "/";
    return null;
  }

  if (file) {
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload.message || "Could not download the report");
    }
    const blob = await response.blob();
    const disposition = response.headers.get("content-disposition") || "";
    const match = /filename="([^"]+)"/.exec(disposition);
    return { blob, fileName: match ? match[1] : "report" };
  }

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/pdf")) {
    if (!response.ok) throw new Error("Could not download the receipt");
    return response.blob();
  }

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || Number(payload.status) >= 400) {
    throw new ApiError(payload.message || "Request failed", {
      errors: payload.errors || [],
      status: Number(payload.status) || response.status,
    });
  }
  return payload;
}

const accountsApi = {
  meta: () => request("/meta"),
  lookups: () => request("/lookups"),
  ledgers: (query) => request("/ledgers", { query }),
  ledger: (id) => request(`/ledgers/${id}`),
  saveLedger: (body, id) => request(id ? `/ledgers/${id}` : "/ledgers", { method: id ? "PUT" : "POST", body }),
  vouchers: (query) => request("/vouchers", { query }),
  voucher: (id) => request(`/vouchers/${id}`),
  saveVoucher: (body) => request("/vouchers", { method: "POST", body }),
  cancelVoucher: (id, reason) => request(`/vouchers/${id}/cancel`, { method: "POST", body: { reason } }),
  outstanding: (ledgerId) => request(`/parties/${ledgerId}/outstanding-invoices`),
  receiptPdf: (id) => request(`/vouchers/${id}/receipt-pdf`),
  cheques: (query) => request("/cheques", { query }),
  chequeStatus: (id, body) => request(`/cheques/${id}/status`, { method: "POST", body }),
  statement: (query) => request("/reports/ledger-statement", { query }),
  downloadReport: (body) => request("/reports/download", { method: "POST", body, file: true }),
  myInvoices: () => request("/portal/my-invoices"),
  myReceipts: () => request("/portal/my-receipts"),
  myStatement: (query) => request("/portal/my-statement", { query }),
  customers: (query) => request("/customers", { query, root: "api" }),
  customerCreditByCode: (customerCode) =>
  request(`/customers/code/${customerCode}/credit-status`, { root: "api" }),
  customerCredit: (id) => request(`/customers/${id}/credit-status`, { root: "api" }),
  saveCustomer: (body, id) => request(id ? `/customers/${id}` : "/customers", { method: id ? "PUT" : "POST", body, root: "api" }),
  orders: (query) => request("/sales/orders", { query, root: "api" }),
  saveOrder: (body) => request("/sales/orders", { method: "POST", body, root: "api" }),
  approveOrder: (id, note) => request(`/sales/orders/${id}/approve`, { method: "POST", body: { note }, root: "api" }),
  cancelOrder: (id, reason) => request(`/sales/orders/${id}/cancel`, { method: "POST", body: { reason }, root: "api" }),
  advices: (query) => request("/sales/loading-advices", { query, root: "api" }),
  saveAdvice: (body) => request("/sales/loading-advices", { method: "POST", body, root: "api" }),
  cancelAdvice: (id, reason) => request(`/sales/loading-advices/${id}/cancel`, { method: "POST", body: { reason }, root: "api" }),
  invoices: (query) => request("/sales/invoices", { query, root: "api" }),
  saveInvoice: (body) => request("/sales/invoices", { method: "POST", body, root: "api" }),
  dayBook: (query) => request("/reports/day-book", { query }),
  outstandingReport: (query) => request("/reports/outstanding", { query }),
  customerOutstanding: (id, query) => request(`/reports/outstanding/${id}`, { query }),
  agingReport: (query) => request("/reports/aging", { query }),
  payablesReport: (query) => request("/reports/payables", { query }),
};

export default accountsApi;
